import useReportSort from '../../components/reports/useReportSort';
import { useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiDownload,
  FiFileText,
  FiLoader,
  FiPercent,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonth() {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
}

function download(blob, fileName) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export default function GSTReportsPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(user, "reports.gst.generate");
  const canExport = hasPermission(user, "reports.gst.export");
  const [filters, setFilters] = useState({
    from: firstDayOfMonth(),
    to: today(),
    direction: "ALL",
  });
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [isGenerating, setIsGenerating] = useState(false);
  const [exporting, setExporting] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return report?.rows || [];
    return (report?.rows || []).filter((row) =>
      [row.documentNumber, row.partyName, row.productName, row.sku, row.hsnCode]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [report, search]);

  const generate = async () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    setError("");
    try {
      const next = await apiRequest("/gst-reports/generate", {
        method: "POST",
        body: filters,
        accessToken,
      });
      setReport(next);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Could not generate the GST report.");
    } finally {
      setIsGenerating(false);
    }
  };

  const exportReport = async (format) => {
    if (!report || !canExport) return;
    setExporting(format);
    setError("");
    try {
      const params = new URLSearchParams({ ...filters, format });
      const response = await apiRequest(`/gst-reports/export?${params}`, {
        accessToken,
        rawResponse: true,
      });
      const disposition = response.headers.get("content-disposition") || "";
      const name =
        disposition.match(/filename="?([^";]+)"?/)?.[1] ||
        `charms-gst.${format === "XLSX" ? "xlsx" : "json"}`;
      download(await response.blob(), name);
    } catch (requestError) {
      setError(requestError.message || "Could not export the GST report.");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="business-report space-y-6">
      <section className="rounded-3xl border border-primary-100 bg-gradient-to-br from-primary-900 to-primary-700 p-6 text-white shadow-sm lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-primary-100">
              <FiPercent /> Statutory reports
            </div>
            <h1 className="text-3xl font-bold">GST Register</h1>
            <p className="mt-2 max-w-2xl text-primary-100">
              Generate a reviewable GST register from posted purchases and
              completed sales, then export it for accounting.
            </p>
          </div>
          <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-primary-50">
            Generate → preview → export
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm lg:p-6">
        <div className="mb-5 flex items-center gap-2 text-lg font-bold text-gray-900">
          <FiFileText className="text-primary-800" /> Report filters
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="text-sm font-semibold text-gray-700">
            From date
            <input
              type="date"
              value={filters.from}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  from: event.target.value,
                }))
              }
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </label>
          <label className="text-sm font-semibold text-gray-700">
            To date
            <input
              type="date"
              value={filters.to}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  to: event.target.value,
                }))
              }
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </label>
          <label className="text-sm font-semibold text-gray-700">
            Register scope
            <select
              value={filters.direction}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  direction: event.target.value,
                }))
              }
              className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            >
              <option value="ALL">Outward and inward supplies</option>
              <option value="OUTWARD">Outward supplies only</option>
              <option value="INWARD">Inward supplies only</option>
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || isGenerating}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-800 px-4 py-3 font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isGenerating ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {isGenerating ? "Generating…" : "Generate preview"}
            </button>
          </div>
        </div>
        {!canGenerate && (
          <p className="mt-4 flex items-center gap-2 text-sm text-amber-700">
            <FiShield /> You can view this report, but do not have permission to
            generate it.
          </p>
        )}
      </section>

      {error && (
        <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {!report ? (
        <section className="rounded-3xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center shadow-sm">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold text-gray-900">
            No preview generated yet
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">
            Choose the reporting period and generate a preview. Export options
            appear only after you review the live results.
          </p>
        </section>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[
              [
                "Taxable value",
                report.summary.taxableValue,
                "text-primary-800",
              ],
              ["CGST", report.summary.cgst, "text-sky-700"],
              ["SGST", report.summary.sgst, "text-emerald-700"],
              ["IGST", report.summary.igst, "text-orange-700"],
              ["Total GST", report.summary.taxAmount, "text-violet-700"],
            ].map(([label, value, color]) => (
              <div
                key={label}
                className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
              >
                <p className="text-sm font-medium text-gray-500">{label}</p>
                <p className={`mt-2 text-2xl font-bold ${color}`}>
                  {money(value)}
                </p>
              </div>
            ))}
          </section>

          {report.validation?.warnings?.map((warning) => (
            <div
              key={warning}
              className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
            >
              <FiAlertCircle className="mt-0.5 shrink-0" />
              {warning}
            </div>
          ))}

          <section className="rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-gray-100 p-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  GST register preview
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {visibleRows.length} line(s) from posted and completed source
                  documents.
                </p>
              </div>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search document, party, product, SKU or HSN"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100 lg:max-w-sm"
              />
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[1550px] w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    {[
                      "Direction",
                      "Document",
                      "Date",
                      "Party",
                      "HSN / Product",
                      "Qty",
                      "Rate",
                      "Taxable",
                      "CGST",
                      "SGST",
                      "IGST",
                      "GST",
                      "Value",
                      "Split",
                    ].map((header) => (
                      <th key={header} className="px-4 py-3 font-semibold">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, index) => (
                    <tr
                      key={`${row.source}-${row.documentNumber}-${row.sku}-${index}`}
                      className="border-t border-gray-100 hover:bg-primary-50/30"
                    >
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.direction === "OUTWARD" ? "bg-rose-50 text-primary-800" : "bg-emerald-50 text-emerald-700"}`}
                        >
                          {row.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-primary-800">
                        {row.documentNumber}
                        <p className="mt-1 text-xs font-normal text-gray-500">
                          {row.source}
                        </p>
                      </td>
                      <td className="px-4 py-3">{row.documentDate}</td>
                      <td className="px-4 py-3">
                        {row.partyName}
                        <p className="mt-1 text-xs text-gray-500">
                          {row.partyGstin || "No GSTIN"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{row.productName}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          {row.hsnCode || "No HSN"} · {row.sku}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {Number(row.quantity || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-right">{row.taxRate}%</td>
                      <td className="px-4 py-3 text-right">
                        {money(row.taxableValue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.cgst)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.sgst)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.igst)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {money(row.taxAmount)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {money(row.invoiceValue)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.taxSplitStatus === "STORED" ? "bg-emerald-50 text-emerald-700" : row.taxSplitStatus === "INFERRED" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}
                        >
                          {row.taxSplitStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Export reviewed report
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Excel is for review. JSON is the structured CHARMS register.
                  Tally JSON contains balanced retail sales and purchase voucher
                  mappings.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {[
                  ["XLSX", "Export Excel"],
                  ["JSON", "Export JSON"],
                  ["TALLY_JSON", "Export Tally JSON"],
                ].map(([format, label]) => (
                  <button
                    key={format}
                    type="button"
                    disabled={!canExport || Boolean(exporting)}
                    onClick={() => exportReport(format)}
                    className="inline-flex items-center gap-2 rounded-xl border border-primary-200 bg-white px-4 py-2.5 text-sm font-semibold text-primary-800 transition hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {exporting === format ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiDownload />
                    )}
                    {exporting === format ? "Preparing…" : label}
                  </button>
                ))}
              </div>
            </div>
            {!canExport && (
              <p className="mt-4 flex items-center gap-2 text-sm text-amber-700">
                <FiShield /> You can preview this report, but do not have export
                permission.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
